/*
Copyright 2024 Milton Moura <miltonmoura@gmail.com>

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { EventType, RelationType } from "matrix-js-sdk";
import {
  createContext,
  use,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type JSX,
} from "react";
import { type MatrixRTCSession } from "matrix-js-sdk/lib/matrixrtc";
import { logger } from "matrix-js-sdk/lib/logger";

import { useMatrixRTCSessionMemberships } from "../useMatrixRTCSessionMemberships";
import { useClientState } from "../ClientContext";
import { ElementCallReactionEventType, type ReactionOption } from ".";
import { type CallViewModel } from "../state/CallViewModel";
import { useBehavior } from "../useBehavior";

interface ReactionsSenderContextType {
  supportsReactions: boolean;
  toggleRaisedHand: () => Promise<void>;
  sendReaction: (reaction: ReactionOption) => Promise<void>;
}

const ReactionsSenderContext = createContext<
  ReactionsSenderContextType | undefined
>(undefined);

export const useReactionsSender = (): ReactionsSenderContextType => {
  const context = use(ReactionsSenderContext);
  if (!context) {
    throw new Error("useReactions must be used within a ReactionsProvider");
  }
  return context;
};

/**
 * Provider that handles sending a reaction or hand raised event to a call.
 */
export const ReactionsSenderProvider = ({
  children,
  rtcSession,
  vm,
}: {
  children: ReactNode;
  rtcSession: MatrixRTCSession;
  vm: CallViewModel;
}): JSX.Element => {
  const memberships = useMatrixRTCSessionMemberships(rtcSession);
  const clientState = useClientState();
  const supportsReactions =
    clientState?.state === "valid" && clientState.supportedFeatures.reactions;
  const room = rtcSession.room;
  const myUserId = room.client.getUserId();
  const myDeviceId = room.client.getDeviceId();
  const myMembershipIdentifier = `${myUserId}:${myDeviceId}`;

  const myMembershipEvent = useMemo(
    () =>
      memberships.find(
        (m) => m.sender === myUserId && m.deviceId === myDeviceId,
      )?.eventId,
    [memberships, myUserId, myDeviceId],
  );

  const reactions = useBehavior(vm.reactions$);
  const myReaction = useMemo(
    () =>
      myMembershipIdentifier !== undefined
        ? reactions[myMembershipIdentifier]
        : undefined,
    [myMembershipIdentifier, reactions],
  );

  const handsRaised = useBehavior(vm.handsRaised$);
  const myRaisedHand = useMemo(
    () =>
      myMembershipIdentifier !== undefined
        ? handsRaised[myMembershipIdentifier]
        : undefined,
    [myMembershipIdentifier, handsRaised],
  );

  /**
   * Чего человек хочет, а не что сейчас на сервере.
   *
   * Реакция «рука» привязана к событию участия в звонке, а оно обновляется само
   * — при переподключении, при смене устройства, по истечении срока. Читатель,
   * увидев новое событие участия, просто СНИМАЛ поднятую руку: реакция висела на
   * устаревшем участии. Со стороны это выглядело так, будто рука не поднялась, а
   * повторное нажатие пропадало бесследно (CTO, 07.09.2026).
   *
   * Держим намерение отдельно и приводим состояние к нему: пока рука поднята,
   * при каждом обновлении участия реакция перевешивается на новое событие.
   */
  const wantsHandRaised = useRef(false);
  const reconciling = useRef(false);

  const raiseHand = useCallback(
    async (membershipEventId: string) => {
      const reaction = await room.client.sendEvent(
        rtcSession.room.roomId,
        EventType.Reaction,
        {
          "m.relates_to": {
            rel_type: RelationType.Annotation,
            event_id: membershipEventId,
            key: "🖐️",
          },
        },
      );
      logger.debug("Sent raise hand event", reaction.event_id);
    },
    [room, rtcSession],
  );

  const lowerHand = useCallback(
    async (reactionEventId: string) => {
      await room.client.redactEvent(rtcSession.room.roomId, reactionEventId);
      logger.debug("Redacted raise hand event");
    },
    [room, rtcSession],
  );

  const toggleRaisedHand = useCallback(async () => {
    if (!myMembershipIdentifier) {
      return;
    }
    const myReactionId = myRaisedHand?.reactionEventId;
    wantsHandRaised.current = !myReactionId;

    try {
      if (!myReactionId) {
        if (!myMembershipEvent) {
          // Своего участия ещё нет — намерение уже записано, руку поднимет
          // согласование ниже, как только участие появится.
          throw new Error("Cannot find own membership event");
        }
        await raiseHand(myMembershipEvent);
      } else {
        await lowerHand(myReactionId);
      }
    } catch (ex) {
      // Раньше ошибка подъёма уходила только в журнал, и нажатие пропадало
      // бесследно. Намерение остаётся записанным, поэтому попытка повторится
      // при следующем обновлении участия.
      logger.error("Не удалось переключить поднятую руку", ex);
    }
  }, [
    lowerHand,
    myMembershipEvent,
    myMembershipIdentifier,
    myRaisedHand,
    raiseHand,
  ]);

  // Согласование: приводим то, что на сервере, к тому, чего человек хочет.
  useEffect(() => {
    if (!myMembershipIdentifier || reconciling.current) return;

    const raisedOn = myRaisedHand?.membershipEventId;
    const needsRaise =
      wantsHandRaised.current &&
      !!myMembershipEvent &&
      raisedOn !== myMembershipEvent;
    const needsLower = !wantsHandRaised.current && !!myRaisedHand;
    if (!needsRaise && !needsLower) return;

    reconciling.current = true;
    void (async () => {
      try {
        if (needsRaise) {
          // Старую реакцию снимаем: она висит на устаревшем участии и всё
          // равно никому не видна, но без снятия останется в комнате.
          if (myRaisedHand?.reactionEventId) {
            await lowerHand(myRaisedHand.reactionEventId);
          }
          await raiseHand(myMembershipEvent!);
        } else if (myRaisedHand?.reactionEventId) {
          await lowerHand(myRaisedHand.reactionEventId);
        }
      } catch (ex) {
        logger.error("Не удалось согласовать поднятую руку", ex);
      } finally {
        reconciling.current = false;
      }
    })();
  }, [
    lowerHand,
    myMembershipEvent,
    myMembershipIdentifier,
    myRaisedHand,
    raiseHand,
  ]);

  const sendReaction = useCallback(
    async (reaction: ReactionOption) => {
      if (!myMembershipIdentifier || myReaction) {
        // We're still reacting
        return;
      }
      if (!myMembershipEvent) {
        throw new Error("Cannot find own membership event");
      }
      await room.client.sendEvent(
        rtcSession.room.roomId,
        ElementCallReactionEventType,
        {
          "m.relates_to": {
            rel_type: RelationType.Reference,
            event_id: myMembershipEvent,
          },
          emoji: reaction.emoji,
          name: reaction.name,
        },
      );
    },
    [myMembershipEvent, myReaction, room, myMembershipIdentifier, rtcSession],
  );

  return (
    <ReactionsSenderContext
      value={{
        supportsReactions,
        toggleRaisedHand,
        sendReaction,
      }}
    >
      {children}
    </ReactionsSenderContext>
  );
};
